import { useEffect, useRef } from 'react'
import { createConsumer, type Consumer } from '@rails/actioncable'

/**
 * Cart item as serialized by PosCartSerializer on the backend.
 * Must match the backend shape exactly.
 */
export interface CartItem {
  id: number
  product_id: number
  variant_id: number | null
  quantity: number
  unit_price: number
  tax_rate: number
  line_subtotal: number
  line_tax: number
  line_total: number
  discount_cents: number
  promotions: { name: string; tier_quantity: number; tier_price_cents: number }[]
  product: {
    id: number
    name: string
    sku: string | null
    price: number
    tax_rate: number
    track_inventory: boolean
    track_variants: boolean
    requires_serial: boolean
    stock_quantity: number
    category_name: string | null
    product_images: { id: string; url: string }[]
    variants: unknown[]
  }
  variant: {
    id: number
    sku: string | null
    price: number
    stock_quantity: number
    option_display: string
  } | null
  display_name: string
}

export interface CartBroadcast {
  type: 'cart_updated' | 'cart_cleared'
  cart: CartItem[]
  broadcast_at: string
}

interface Options {
  /** Called when the channel receives a message. */
  onMessage: (payload: CartBroadcast) => void
  /** Called when the WebSocket reconnects after a drop. */
  onReconnect?: () => void
  /** Disable the connection (e.g., while not in POS). Default: false */
  enabled?: boolean
}

/**
 * Subscribes to PosCartChannel for the current user.
 * On disconnect, Action Cable auto-reconnects. We surface the
 * reconnect event so the consumer can refetch the cart from the server
 * (in case messages were missed during the gap).
 */
export function usePosCartChannel({ onMessage, onReconnect, enabled = true }: Options) {
  const consumerRef = useRef<Consumer | null>(null)
  const onMessageRef = useRef(onMessage)
  const onReconnectRef = useRef(onReconnect)

  // Keep refs current so we don't re-subscribe on every render
  useEffect(() => { onMessageRef.current = onMessage }, [onMessage])
  useEffect(() => { onReconnectRef.current = onReconnect }, [onReconnect])

  useEffect(() => {
    if (!enabled) return

    const consumer = createConsumer()
    consumerRef.current = consumer

    const subscription = consumer.subscriptions.create(
      { channel: 'PosCartChannel' },
      {
        connected() {
          // Initial connect — no need to refetch, the page just loaded
        },
        disconnected() {
          // Will auto-reconnect. We don't trigger refetch here.
        },
        received(data: CartBroadcast) {
          onMessageRef.current(data)
        }
      }
    )

    // Action Cable's reconnect is internal; to detect it we'd need to
    // hook into the underlying WebSocket. For now, we rely on the page-level
    // `useFocusRefetch` to also fetch on focus/visibilitychange.
    // (If we needed real "I just reconnected" detection, we could
    // monkey-patch consumer.subscriptions.create, but that's overkill.)

    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
      consumerRef.current = null
    }
  }, [enabled])
}
